// src/components/warranties/WarrantyCatalogByCategory.jsx
import React, { useMemo, useState, useRef } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function parseISODate(iso) {
    const s = String(iso || "").slice(0, 10);
    if (!s) return null;
    const [y, m, d] = s.split("-").map((n) => Number(n));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

function addMonths(date, months) {
    const d = new Date(date.getTime());
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);

    // ajuste p/ meses com menos dias
    if (d.getDate() !== day) d.setDate(0);
    return d;
}

function fmtDateBR(iso) {
    const s = String(iso || "").slice(0, 10);
    if (!s) return "-";
    const [y, m, d] = s.split("-");
    if (!y || !m || !d) return s;
    return `${d}/${m}/${y}`;
}

function normalizeModelName(s) {
    const v = String(s || "").trim();
    if (!v) return "sem categoria";
    return v.toLowerCase();
}

function titleCase(s) {
    return String(s || "")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

function getWarrantyState(kitPayDateISO) {
    const dt = parseISODate(kitPayDateISO);
    if (!dt) return { ok: false, label: "Sem data", tooltip: "Sem kit__pay_date" };

    const end = addMonths(dt, 12);
    const now = new Date();
    // considera o dia inteiro
    end.setHours(23, 59, 59, 999);

    const ok = now <= end;
    return {
        ok,
        label: ok ? "Dentro da garantia" : "Fora da garantia",
        tooltip: ok
            ? "Até 12 meses após a data do kit"
            : "Passou de 12 meses após a data do kit",
    };
}

export default function WarrantyCatalogByCategory({
    products,
    theme,
    colors,
    subtleBorder,
    softText,
    selectedPk,
    onSelect,
}) {
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState("");
    const topRef = useRef(null);


    const groups = useMemo(() => {
        const list = Array.isArray(products) ? products : [];
        const q = String(query || "").trim().toLowerCase();

        // filtro (bem flexível)
        const filtered = !q
            ? list
            : list.filter((p) => {
                const code = String(p.product_id_produto || "").toLowerCase();
                const model = String(p.content_type__model || "").toLowerCase();
                const kit = String(p.kit__kit_number ?? "").toLowerCase();
                const date = String(p.kit__pay_date || "").toLowerCase();
                return (
                    code.includes(q) ||
                    model.includes(q) ||
                    kit.includes(q) ||
                    date.includes(q)
                );
            });

        // agrupa por model
        const map = new Map();
        for (const p of filtered) {
            const key = normalizeModelName(p.content_type__model);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(p);
        }

        // ordena items dentro do grupo por mais recente
        const entries = Array.from(map.entries()).map(([k, arr]) => {
            const sorted = [...arr].sort((a, b) => {
                const da = parseISODate(a.kit__pay_date)?.getTime() || 0;
                const db = parseISODate(b.kit__pay_date)?.getTime() || 0;
                return db - da;
            });
            return [k, sorted];
        });

        // ordena grupos por quantidade desc (p/ “mais usado” no topo)
        entries.sort((a, b) => (b[1]?.length || 0) - (a[1]?.length || 0));

        return entries;
    }, [products, query]);

    const total = useMemo(() => (Array.isArray(products) ? products.length : 0), [products]);

    return (
        <Paper
            variant="outlined"
            ref={topRef}
            sx={{
                mt: 2,
                borderRadius: 2,
                border: subtleBorder,
                bgcolor: theme?.palette?.mode === "dark" ? colors?.pink?.[700] : "#fafafa",
            }}
        >
            <Accordion
                expanded={expanded}
                onChange={(_, v) => {
                    setExpanded(v);

                    if (!v) {
                        window.scrollTo({ top: 0 });
                    }
                }}
                disableGutters
                TransitionProps={{
                    timeout: { enter: 600, exit: 250 },
                }}
                sx={{
                    bgcolor: "transparent",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                    >
                        <Stack spacing={0.25}>
                            <Typography fontWeight={900}>Catálogo por foto</Typography>
                            <Typography variant="body2" color={softText}>
                                Organizado por categoria. Inclui itens fora do prazo (para você achar histórico antigo).
                            </Typography>
                        </Stack>

                        <Chip size="small" variant="outlined" label={`${total} itens`} />
                    </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />

                    <TextField
                        fullWidth
                        size="small"
                        label="Buscar no catálogo (código, categoria, kit, data)"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />

                    <Box sx={{ mt: 2 }}>
                        {groups.map(([modelKey, items]) => {
                            const title = titleCase(modelKey);

                            // contagem dentro vs fora
                            const counts = items.reduce(
                                (acc, p) => {
                                    const st = getWarrantyState(p.kit__pay_date);
                                    if (st.ok) acc.in += 1;
                                    else acc.out += 1;
                                    return acc;
                                },
                                { in: 0, out: 0 }
                            );

                            return (
                                <Box key={modelKey} sx={{ mb: 2 }}>
                                    <Divider sx={{ marginBottom: '10px' }} />
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
                                        <Typography variant="h2" fontWeight={900}>{title}</Typography>
                                        <Chip size="small" variant="outlined" label={`${items.length}`} />
                                        <Chip size="small" color="success" variant="outlined" label={`${counts.in} dentro`} />
                                        <Chip size="small" color="warning" variant="outlined" label={`${counts.out} fora`} />
                                    </Stack>

                                    <Grid container spacing={1.25}>
                                        {items.map((p) => {
                                            const pk = p.pk;
                                            const isSelected = String(selectedPk) === String(pk);

                                            const st = getWarrantyState(p.kit__pay_date);

                                            return (
                                                <Grid item xs={6} sm={4} md={3} lg={2} key={pk}>
                                                    <Box
                                                        onClick={() => {
                                                            setExpanded(false)
                                                            onSelect?.(p)
                                                        }}
                                                        sx={{
                                                            cursor: "pointer",
                                                            borderRadius: 2,
                                                            border: isSelected ? `2px solid ${colors?.greenAccent?.[400] || "rgba(0,0,0,0.3)"}` : subtleBorder,
                                                            overflow: "hidden",
                                                            bgcolor: theme?.palette?.mode === "dark" ? colors?.pink?.[800] : "#fff",
                                                            transition: "transform 120ms ease",
                                                            "&:hover": { transform: "translateY(-2px)" },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: "100%",
                                                                aspectRatio: "1 / 1",
                                                                bgcolor: theme?.palette?.mode === "dark" ? colors?.pink?.[900] : "rgba(0,0,0,0.03)",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {p.imageUrl ? (
                                                                <img
                                                                    src={p.imageUrl}
                                                                    alt={p.product_id_produto || "produto"}
                                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                    loading="lazy"
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color={softText}>
                                                                    Sem foto
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        <Box sx={{ p: 1 }}>
                                                            <Stack spacing={0.5}>
                                                                <Typography variant="body2" fontWeight={900} noWrap title={String(p.product_id_produto || "")}>
                                                                    #{p.product_id_produto || "-"}
                                                                </Typography>

                                                                <Typography variant="caption" color={softText} noWrap title={`Kit #${p.kit__kit_number ?? "-"}`}>
                                                                    Kit #{p.kit__kit_number ?? "-"} • {fmtDateBR(p.kit__pay_date)}
                                                                </Typography>

                                                                <Tooltip title={st.tooltip}>
                                                                    <Chip
                                                                        size="small"
                                                                        color={st.ok ? "success" : "warning"}
                                                                        variant="outlined"
                                                                        label={st.ok ? "GARANTIA OK" : "FORA PRAZO"}
                                                                        sx={{ width: "fit-content" }}
                                                                    />
                                                                </Tooltip>
                                                            </Stack>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            );
                        })}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
}
